import { env } from "@/app/env";
import type { ApiErrorBody } from "@/api/types";
import type { AuthTokenSnapshot } from "@/auth/token-session";

export const authReplayClasses = [
  "pure-safe-retrieval",
  "retryable-idempotent-materializing-lookup",
  "server-idempotent-mutation",
  "never-replay"
] as const;

export type AuthReplayClass = (typeof authReplayClasses)[number];

export type AuthTransport = {
  getSnapshot: (forceRefresh?: boolean) => Promise<AuthTokenSnapshot>;
  currentSnapshot: () => AuthTokenSnapshot | null;
  onTerminalUnauthorized: (rejected: AuthTokenSnapshot) => void;
};

let authTransport: AuthTransport | null = null;
const forcedRefreshByGeneration = new Map<string, Promise<AuthTokenSnapshot>>();

export function setAuthTransport(transport: AuthTransport | null) {
  authTransport = transport;
  forcedRefreshByGeneration.clear();
}

export class ApiError extends Error {
  status: number;
  code: string;
  requestId: string;

  constructor(status: number, code: string, message: string, requestId = "") {
    super(`${status} ${code}: ${message}`);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export async function apiRequest<T>(path: string, replayClass: AuthReplayClass, init: RequestInit = {}): Promise<T> {
  return apiRequestAtBase<T>(env.apiBaseUrl, path, replayClass, init);
}

export async function apiRequestAtBase<T>(
  baseUrl: string,
  path: string,
  replayClass: AuthReplayClass,
  init: RequestInit = {}
): Promise<T> {
  if (!authReplayClasses.includes(replayClass)) {
    throw new ApiError(0, "auth_replay_policy_missing", "Request replay policy is not configured");
  }
  const transport = authTransport;
  if (!transport) {
    throw new ApiError(401, "token_provider_missing", "Token provider is not configured");
  }

  const originalSnapshot = await transport.getSnapshot(false);
  const response = await send(baseUrl, path, init, originalSnapshot);
  if (response.status !== 401) {
    return decodeResponse<T>(response);
  }

  let refreshed: AuthTokenSnapshot;
  try {
    refreshed = await refreshAfterUnauthorized(transport, originalSnapshot);
  } catch {
    notifyTerminalUnauthorized(transport, originalSnapshot);
    throw new ApiError(401, "authentication_refresh_failed", "Authentication refresh failed");
  }

  if (replayClass === "never-replay") {
    throw new ApiError(401, "authentication_retry_required", "Authentication was renewed; retry this action explicitly");
  }

  const retriedResponse = await send(baseUrl, path, init, refreshed);
  if (retriedResponse.status === 401) {
    notifyTerminalUnauthorized(transport, refreshed);
  }
  return decodeResponse<T>(retriedResponse);
}

async function send(baseUrl: string, path: string, init: RequestInit, snapshot: AuthTokenSnapshot) {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("Authorization", `Bearer ${snapshot.token}`);
  if (!(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(`${baseUrl}${path}`, {
    ...init,
    headers
  });
}

function refreshAfterUnauthorized(transport: AuthTransport, rejected: AuthTokenSnapshot) {
  const current = transport.currentSnapshot();
  if (current && current.sessionId !== rejected.sessionId) {
    return Promise.reject(new Error("authentication session changed"));
  }
  if (current && current.sessionId === rejected.sessionId && current.generation > rejected.generation) {
    return Promise.resolve(current);
  }

  const key = generationKey(rejected);
  const existing = forcedRefreshByGeneration.get(key);
  if (existing) {
    return existing;
  }

  const refresh = transport.getSnapshot(true);
  forcedRefreshByGeneration.set(key, refresh);
  return refresh;
}

function notifyTerminalUnauthorized(transport: AuthTransport, rejected: AuthTokenSnapshot) {
  const current = transport.currentSnapshot();
  if (current && current.sessionId === rejected.sessionId && current.generation === rejected.generation) {
    transport.onTerminalUnauthorized(rejected);
  }
}

function generationKey(snapshot: AuthTokenSnapshot) {
  return `${snapshot.sessionId}:${snapshot.generation}`;
}

async function decodeResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await apiError(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

async function apiError(response: Response) {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = {};
  }
  const error = body.error;
  const message = Array.isArray(body.message) ? body.message.join("; ") : body.message;
  return new ApiError(
    response.status,
    error?.code || "http_error",
    error?.message || message || response.statusText,
    error?.requestId || ""
  );
}
